import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, ArrowLeft, ShieldCheck, KeyRound, CheckCircle2 } from "lucide-react";
import { api } from "../api";
import PasswordInput from "../components/PasswordInput";

const inputCls =
  "w-full bg-[#12131f] border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white text-sm outline-none focus:border-[#25D366] transition-colors";

const AdminLogin = ({ onLogin, onVerify2fa }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | forgot | otp | twofa
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // 2fa state
  const [ticket, setTicket] = useState("");
  const [code, setCode] = useState("");

  // forgot / otp state
  const [forgotMsg, setForgotMsg] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [resetDone, setResetDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await onLogin(email, password);
    setLoading(false);
    if (res?.twofa) {
      setTicket(res.ticket);
      setMode("twofa");
      return;
    }
    if (res?.ok) {
      navigate("/admin");
    } else {
      setError("Invalid email or password");
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await onVerify2fa(ticket, code);
    setLoading(false);
    if (res?.ok) {
      navigate("/admin");
    } else {
      setError("Invalid authentication code");
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    setForgotMsg("");
    setLoading(true);
    try {
      const data = await api.forgot(email);
      setLoading(false);
      setForgotMsg(data.message || "If the account exists, a 6-digit reset code has been emailed.");
      setMode("otp");
    } catch (err) {
      setLoading(false);
      setForgotMsg("If the account exists, a 6-digit reset code has been emailed.");
      setMode("otp");
    }
  };

  const submitResetOtp = async (e) => {
    e.preventDefault();
    setError("");
    if (otpCode.length !== 6) return setError("Enter the 6-digit code from your email");
    if (newPass.length < 6) return setError("Password must be at least 6 characters");
    if (newPass !== confirmPass) return setError("Passwords do not match");
    setLoading(true);
    try {
      await api.resetOtp(email, otpCode, newPass);
      setLoading(false);
      setResetDone(true);
    } catch (err) {
      setLoading(false);
      setError(err?.response?.data?.detail || "Invalid or expired code");
    }
  };

  const backToLogin = () => {
    setMode("login");
    setError("");
    setForgotMsg("");
    setOtpCode("");
    setNewPass("");
    setConfirmPass("");
    setResetDone(false);
  };

  const titles = { login: "Admin Login", twofa: "Two-Factor Auth", forgot: "Reset Password", otp: "Enter Reset Code" };
  const subtitles = {
    login: "Sign in to manage your sites",
    twofa: "Enter the 6-digit code from your authenticator app",
    forgot: "We'll email a 6-digit reset code",
    otp: "Enter the code we emailed and your new password",
  };

  return (
    <div className="dot-grid min-h-screen flex items-center justify-center px-5">
      <div className="w-full max-w-md bg-[#1b1d2b] border border-white/10 rounded-2xl p-8 shadow-2xl fade-in-up" data-testid="admin-login-card">
        <div className="text-center mb-7">
          <img
            src="/logos/max66-header.png"
            alt="MAX66"
            data-testid="admin-login-logo"
            className="w-44 sm:w-52 h-auto object-contain mx-auto mb-3"
          />
          {!resetDone && (
            <>
              <h1 className="font-display font-bold text-2xl text-white">{titles[mode]}</h1>
              <p className="text-white/50 text-sm mt-1">{subtitles[mode]}</p>
            </>
          )}
        </div>

        {resetDone ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 className="w-12 h-12 text-[#25D366] mx-auto mb-4" />
            <h1 className="font-display font-bold text-2xl text-white">Password Reset</h1>
            <p className="text-white/60 text-sm mt-2">Your password has been updated successfully.</p>
            <button onClick={backToLogin} data-testid="reset-done-login-btn" className="mt-6 w-full bg-[#25D366] hover:bg-[#1fb757] text-white font-display font-semibold py-2.5 rounded-lg transition-colors">
              Go to Login
            </button>
          </div>
        ) : mode === "twofa" ? (
          <form onSubmit={submit2fa} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-white/60 mb-1.5 block">Authentication Code</label>
              <div className="relative">
                <ShieldCheck className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoFocus
                  data-testid="twofa-code-input"
                  className={`${inputCls} tracking-[0.4em] text-center`}
                  placeholder="000000"
                />
              </div>
            </div>
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <button type="submit" disabled={loading || code.length !== 6} data-testid="twofa-submit-btn" className="w-full bg-[#25D366] hover:bg-[#1fb757] disabled:opacity-60 text-white font-display font-semibold py-2.5 rounded-lg transition-colors">
              {loading ? "Verifying..." : "Verify & Sign In"}
            </button>
            <button type="button" onClick={() => { setMode("login"); setCode(""); setError(""); }} className="w-full text-sm text-white/60 hover:text-white transition-colors inline-flex items-center justify-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Back to login
            </button>
          </form>
        ) : mode === "login" ? (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-white/60 mb-1.5 block">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" data-testid="login-email-input" className={inputCls} placeholder="you@gmail.com" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-white/60 mb-1.5 block">Password</label>
              <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" />
            </div>
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full bg-[#25D366] hover:bg-[#1fb757] disabled:opacity-60 text-white font-display font-semibold py-2.5 rounded-lg transition-colors">
              {loading ? "Signing in..." : "Sign In"}
            </button>
            <button type="button" onClick={() => { setMode("forgot"); setError(""); }} data-testid="forgot-password-btn" className="w-full text-sm text-white/60 hover:text-white transition-colors">
              Forgot password?
            </button>
          </form>
        ) : mode === "forgot" ? (
          <form onSubmit={submitForgot} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-white/60 mb-1.5 block">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" data-testid="forgot-email-input" className={inputCls} placeholder="you@gmail.com" />
              </div>
            </div>
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <button type="submit" disabled={loading} data-testid="forgot-submit-btn" className="w-full bg-[#25D366] hover:bg-[#1fb757] disabled:opacity-60 text-white font-display font-semibold py-2.5 rounded-lg transition-colors">
              {loading ? "Sending..." : "Send Reset Code"}
            </button>
            <button type="button" onClick={backToLogin} className="w-full text-sm text-white/60 hover:text-white transition-colors inline-flex items-center justify-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Back to login
            </button>
          </form>
        ) : (
          <form onSubmit={submitResetOtp} className="space-y-4">
            {forgotMsg && <p className="text-white/70 text-sm" data-testid="otp-info-msg">{forgotMsg}</p>}
            <div>
              <label className="text-xs font-medium text-white/60 mb-1.5 block">6-Digit Code</label>
              <div className="relative">
                <KeyRound className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoFocus
                  data-testid="otp-code-input"
                  className={`${inputCls} tracking-[0.4em] text-center`}
                  placeholder="000000"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-white/60 mb-1.5 block">New Password</label>
              <PasswordInput value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder="At least 6 characters" testId="otp-new-password-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-white/60 mb-1.5 block">Confirm Password</label>
              <PasswordInput value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} placeholder="Re-enter password" testId="otp-confirm-password-input" />
            </div>
            {error && <p className="text-red-400 text-sm" data-testid="otp-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="otp-submit-btn" className="w-full bg-[#25D366] hover:bg-[#1fb757] disabled:opacity-60 text-white font-display font-semibold py-2.5 rounded-lg transition-colors">
              {loading ? "Resetting..." : "Reset Password"}
            </button>
            <button type="button" onClick={submitForgot} disabled={loading} className="w-full text-sm text-white/50 hover:text-white transition-colors">
              Resend code
            </button>
            <button type="button" onClick={backToLogin} className="w-full text-sm text-white/60 hover:text-white transition-colors inline-flex items-center justify-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Back to login
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;
